"use client";

import { Command as Palette } from "cmdk";
import { LayoutGrid, Search } from "lucide-react";
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useState } from "react";
import { useOpenApp } from "@/components/app-shell/app-tabs/app-tabs-bar";
import { Button } from "@/components/ui/button";
import { CommandDialog } from "@/components/ui/command";
import { APP_MODULES } from "./app-modules";

export type SpotlightLabels = { apps: string; search: string; none: string; hint: string; shortcut: string };

/** The apps sit in a grid, so Left and Right (swapped in right-to-left text) move through them too: they are passed on to the list as Down and Up. */
function moveAcross(e: ReactKeyboardEvent) {
  if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
  const rtl = getComputedStyle(e.currentTarget).direction === "rtl";
  const forward = (e.key === "ArrowRight") !== rtl;
  e.preventDefault();
  e.currentTarget.dispatchEvent(new KeyboardEvent("keydown", { key: forward ? "ArrowDown" : "ArrowUp", bubbles: true }));
}

/** The app switcher as a search window in the middle of the screen, like Spotlight: a button in the bar (or Ctrl/⌘ + K) opens it, you type to narrow the apps, which show as icons in a grid, move with the arrow keys and press Enter (or click) to open one through the tabs; Escape closes it. Square, like the rest of the bar. `apps` are the ones the person may open, named in their language. */
export function AppSpotlight({ apps, labels }: { apps: { id: string; name: string }[]; labels: SpotlightLabels }) {
  const [open, setOpen] = useState(false);
  const openApp = useOpenApp();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setOpen((o) => !o); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <>
      <Button type="button" variant="ghost" size="icon" aria-label={labels.apps} aria-keyshortcuts="Control+K Meta+K" title={`${labels.apps} (${labels.shortcut})`} onClick={() => setOpen(true)}><LayoutGrid /></Button>
      <CommandDialog open={open} onOpenChange={setOpen} title={labels.apps} description={labels.search} className="top-[18%] max-w-xl rounded-lg! border">
        <Palette onKeyDown={moveAcross} className="flex flex-col rounded-lg! bg-popover text-popover-foreground">
          <div className="flex items-center gap-3 border-b px-4">
            <Search aria-hidden className="size-5 shrink-0 text-muted-foreground" />
            <Palette.Input autoFocus placeholder={labels.search} aria-label={labels.search} className="h-14 w-full bg-transparent text-lg outline-hidden placeholder:text-muted-foreground" />
          </div>
          <Palette.List className="max-h-80 overflow-y-auto p-2">
            <Palette.Empty className="py-8 text-center text-sm text-muted-foreground">{labels.none}</Palette.Empty>
            <div className="grid grid-cols-3 gap-1 sm:grid-cols-4">
              {apps.map(({ id, name }) => {
                const Icon = APP_MODULES.find((m) => m.id === id)?.icon ?? LayoutGrid;
                return (
                  <Palette.Item key={id} value={name} onSelect={() => { setOpen(false); openApp(id); }} className="flex cursor-default flex-col items-center gap-2 rounded-lg px-2 py-4 text-center text-sm select-none data-[selected=true]:bg-accent data-[selected=true]:outline-2 data-[selected=true]:outline-primary">
                    <span className="grid size-14 place-items-center rounded-xl bg-primary text-primary-foreground"><Icon aria-hidden className="size-7" /></span>
                    <span className="w-full break-words">{name}</span>
                  </Palette.Item>
                );
              })}
            </div>
          </Palette.List>
          <p className="border-t px-4 py-2 text-xs text-muted-foreground">{labels.hint}</p>
        </Palette>
      </CommandDialog>
    </>
  );
}
